import { FC, ReactNode } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import {
  StaticPageLayout,
  Section,
  PageHero,
  CtaBanner,
  Callout,
  HEADER_OFFSET,
} from 'app/components/StaticPages';
import { PHONE_NUMBER, PHONE_HREF } from 'app/core/constants';
import { AGREEMENT_INTRO, AGREEMENT_SECTIONS } from './content';

const LegalHeading: FC<{ id: string; children: ReactNode }> = ({
  id,
  children,
}) => (
  <Typography
    variant="h3"
    component="h2"
    id={id}
    sx={{
      scrollMarginTop: `${HEADER_OFFSET}px`,
      mt: 5,
      mb: 2,
      color: 'staticPages.navy',
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
    }}
  >
    <Box
      component="span"
      sx={{
        width: 8,
        height: 24,
        borderRadius: 1,
        bgcolor: 'staticPages.orange',
        display: 'inline-block',
        flexShrink: 0,
      }}
    />
    {children}
  </Typography>
);

const Toc = () => (
  <Box
    component="aside"
    sx={{
      position: { md: 'sticky' },
      top: { md: HEADER_OFFSET },
      alignSelf: 'start',
    }}
  >
    <Typography
      variant="eyebrow"
      sx={{ color: 'staticPages.text.light', fontWeight: 700, mb: 2 }}
    >
      On This Page
    </Typography>
    <Box
      component="nav"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        borderLeft: 2,
        borderColor: 'staticPages.border.hero',
        pl: 2,
      }}
    >
      {AGREEMENT_SECTIONS.map(section => (
        <Box
          key={section.id}
          component="a"
          href={`#${section.id}`}
          sx={{
            typography: 'body2',
            fontWeight: 500,
            color: 'staticPages.text.secondary',
            textDecoration: 'none',
            '&:hover': { color: 'staticPages.navy' },
          }}
        >
          {section.toc}
        </Box>
      ))}
    </Box>
  </Box>
);

const RentalAgreement = () => (
  <StaticPageLayout
    title="Rental Agreement | Save up to 50% on Stroller Rentals"
    metaDescription="Save up to 50% on your next Orlando stroller rental. Disney fees are too expensive, contact Kingdom Strollers today and schedule a delivery!"
  >
    <PageHero
      eyebrow="Terms & Policies"
      title="Rental Agreement & Liability Waiver"
      subtitle="Please read these terms carefully. By accepting your reservation you agree to everything set out below."
      subtitleMaxWidth={640}
    />

    <Section
      innerSx={{
        pt: { xs: 4, md: 6 },
        pb: 3,
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '240px 1fr' },
        gap: { xs: 4, md: 6.5 },
        alignItems: 'start',
      }}
    >
      <Toc />

      <Box
        component="article"
        sx={{
          typography: 'body2',
          minWidth: 0,
          lineHeight: 1.75,
          color: 'staticPages.text.secondary',
        }}
      >
        <Callout sx={{ mb: 1 }}>{AGREEMENT_INTRO}</Callout>

        {AGREEMENT_SECTIONS.map(section => (
          <Box key={section.id} component="section">
            <LegalHeading id={section.id}>{section.heading}</LegalHeading>
            {section.body}
          </Box>
        ))}
      </Box>
    </Section>

    <Section innerSx={{ pt: 3, pb: { xs: 6, md: 9 } }}>
      <CtaBanner
        body={
          <Box component="span" sx={{ color: 'staticPages.text.onCtaLight' }}>
            Questions about these terms? We&apos;re happy to walk you through
            anything before you book.
          </Box>
        }
        cta={{ label: `Call ${PHONE_NUMBER}`, href: PHONE_HREF }}
        sx={{ py: { md: 5 } }}
      />
    </Section>
  </StaticPageLayout>
);

export default RentalAgreement;
